// @ts-strict-ignore
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, ButtonSize, ButtonVariant } from '@usealloy/component-library';
import { useAppDispatch } from '~/store/reduxHooks';
import { IsolatedEditorContainer } from 'flow/editor/styles';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import { THRESHOLD_LIMIT } from 'flow/editor/components/MenuBar/MenuBarModals/ValidateAndSaveVersionModals/validateRegex';
import { ThresholdRightAttributeOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import { addTrailingSpaces, scrubTrailingSpaces } from 'flow/editor/components/thresholds/RuleThresholdDropdown/helpers';
import { selectValidator } from 'flow/editor/components/thresholds/IsolatedTextEditingContainer/helpers';
import ValidityBadge from './ValidityBadge';
import { getEditorOriginType, getRightAttributeValueAsString } from './helpers';
import { StyledTextArea, StyledEditorButton } from './styles';
import { ThresholdContext } from '../context';
import { useKeyboardKeyEvent } from 'flow/editor/components/WorkflowCanvas/hooks';
import { resetSubEditor } from 'flow/store/redux/thunks';
import { RuleBuilderContext } from '../../../context';

function IsolatedTextEditor() {
  const dispatch = useAppDispatch();
  const { keyboardFocus } = useContext(RuleBuilderContext);
  const { lhsDataType, populatedThreshold } = useContext(ThresholdContext);
  const { publicId, operator } = populatedThreshold;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const originType = getEditorOriginType({ lhsDataType, operator });

  const isRegexEditor = (
    originType === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX
  );
  const isListInclusionEditor = (
    // requires csv style comma delineation format handling
    originType === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_LIST_INCLUSION
  );

  const supportsValidityChecks = isRegexEditor || (
    isListInclusionEditor && (lhsDataType === 'decimal' || lhsDataType === 'integer')
  );

  const stringifiedValue = getRightAttributeValueAsString(populatedThreshold.right.attributeValue);

  const originalTextValue = isListInclusionEditor
    ? addTrailingSpaces(stringifiedValue) // add trailing spaces to comma delineated string
    : stringifiedValue;

  const [textValue, setTextValue] = useState<string>(originalTextValue);
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => textAreaRef?.current?.focus(), []);

  useEffect(() => {
    const dispatchedValue = isListInclusionEditor
      ? scrubTrailingSpaces(textValue)
      : textValue;

    dispatch(updateThreshold.rightValue(publicId, dispatchedValue.trim()));
  }, [dispatch, isListInclusionEditor, publicId, textValue]);

  function closeSubEditor() {
    dispatch(resetSubEditor());
    keyboardFocus.enable();
  }

  function onEnterKey() {
    if (isValid) closeSubEditor();
  }

  useKeyboardKeyEvent('Enter', onEnterKey);

  const textLength = isListInclusionEditor
    ? scrubTrailingSpaces(textValue).length
    : textValue.length;

  const textEditLimitErrors = textLength >= THRESHOLD_LIMIT
    ? `${textValue.length > THRESHOLD_LIMIT ? 'Exceeds' : 'Reached'} the maximum character limit of ${THRESHOLD_LIMIT} characters.`
    : null;

  // scrub pasted input newline delineators. Not safe for string lists or regex strings
  const scrubNewlines = (text: string) => text.replace(/[\r\n]+/g, '');
  const getTextValue = (text: string) => (
    lhsDataType !== 'string'
      ? scrubNewlines(text)
      : text
  );

  const validator = selectValidator(originType, lhsDataType);

  const onInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nativeEvent = e.nativeEvent as InputEvent;
    if (nativeEvent.inputType === 'insertLineBreak') {
      return;
    }

    const text = getTextValue(e.target.value);
    setTextValue(text);

    if (supportsValidityChecks) {
      const isNewValueValid = validator(text);
      if (isNewValueValid !== isValid) setIsValid(isNewValueValid);
    }
  };

  return (
    <IsolatedEditorContainer>
      <StyledTextArea
        isRegexEditor={isRegexEditor}
        ref={textAreaRef}
        value={textValue}
        error={textEditLimitErrors}
        onInput={onInput}
      />
      <Box
        justifyContent="flex-end"
        gap={2}
        style={{ padding: 'var(--cl-space-1)' }}
      >
        {supportsValidityChecks && (
          <ValidityBadge
            isValid={isValid}
            originType={originType}
            leftDataType={lhsDataType}
          />
        )}
        <StyledEditorButton
          disabled={!isValid}
          variant={ButtonVariant.primary}
          size={ButtonSize.small}
          onClick={() => closeSubEditor()}
        >
          Done
        </StyledEditorButton>
      </Box>
    </IsolatedEditorContainer>
  );
}

export default IsolatedTextEditor;
